import { strToU8, zipSync } from "fflate";
import type { InstallationReference } from "@shared/finance/manualExpenses";

export const ALLOWANCES_EXPORT_HEADERS = [
  "N.º expediente",
  "Cliente",
  "Técnico",
  "Categoría",
  "N.º factura o ticket",
  "Fecha",
  "Proveedor",
  "Importe",
  "Estado",
  "Enlace",
] as const;

type Expense = {
  id?: number;
  date?: string | null;
  uploadedByName?: string | null;
  installationId?: number | null;
  installationReference?: InstallationReference | null;
  category: string;
  amount?: string | number | null;
  status: string;
  vendor?: string | null;
  documentNumber?: string | null;
};

export type AllowanceSortDirection = "asc" | "desc";

export function allowanceTicketLabel(value?: string | null): string {
  const number = value?.trim();
  return !number || number.toUpperCase() === "S/N"
    ? "Número pendiente"
    : number;
}

export function sortAllowanceRows<T>(
  rows: readonly T[],
  direction: AllowanceSortDirection,
  getValue: (row: T) => string | number | null | undefined
): T[] {
  const collator = new Intl.Collator("es", {
    numeric: true,
    sensitivity: "base",
  });
  return rows
    .map((value, index) => ({ value, index, sortValue: getValue(value) }))
    .sort((left, right) => {
      const a = left.sortValue ?? "";
      const b = right.sortValue ?? "";
      const aBlank = typeof a === "string" && a.trim() === "";
      const bBlank = typeof b === "string" && b.trim() === "";
      if (aBlank !== bBlank) return aBlank ? 1 : -1;
      const comparison =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : collator.compare(String(a), String(b));
      return (
        (direction === "desc" ? -comparison : comparison) ||
        left.index - right.index
      );
    })
    .map(({ value }) => value);
}

export function mapAllowanceExportRows(
  expenses: Expense[],
  formatInstallation: (expense: Expense) => string,
  getCategoryName: (category: string) => string,
  statusLabels: Record<string, string>
): (string | number)[][] {
  return expenses.map(expense => {
    return [
      formatInstallation(expense),
      expense.installationReference?.clientName ?? "—",
      expense.uploadedByName ?? "—",
      getCategoryName(expense.category),
      allowanceTicketLabel(expense.documentNumber),
      expense.date ?? "",
      expense.vendor ?? "—",
      Number.parseFloat(String(expense.amount ?? "0")) || 0,
      statusLabels[expense.status] ?? expense.status,
      expense.installationId ? "Ver instalación" : "—",
    ];
  });
}

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnName(index: number): string {
  let value = index + 1;
  let name = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

function excelDateSerial(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  )
    return null;
  return Math.round((date.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000);
}

function cellXml(
  value: string | number,
  rowIndex: number,
  columnIndex: number
): string {
  const reference = `${columnName(columnIndex)}${rowIndex}`;
  if (typeof value === "number" && Number.isFinite(value)) {
    const style = columnIndex === 7 ? ' s="2"' : "";
    return `<c r="${reference}"${style}><v>${value}</v></c>`;
  }
  const text = String(value ?? "");
  if (columnIndex === 5) {
    const serial = excelDateSerial(text);
    if (serial !== null)
      return `<c r="${reference}" s="1"><v>${serial}</v></c>`;
  }
  return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(text)}</t></is></c>`;
}

function tableColumnXml(name: string, index: number): string {
  return `<tableColumn id="${index + 1}" name="${xmlEscape(name)}"/>`;
}

export function buildAllowancesXlsx(rows: (string | number)[][]): Uint8Array {
  const lastRow = rows.length + 1;
  const lastColumn = columnName(ALLOWANCES_EXPORT_HEADERS.length - 1);
  const tableRange = `A1:${lastColumn}${lastRow}`;
  const allRows = [ALLOWANCES_EXPORT_HEADERS as readonly string[], ...rows];
  const sheetRows = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((value, columnIndex) => cellXml(value, rowIndex + 1, columnIndex))
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");
  const columnWidths = [22, 28, 24, 26, 26, 15, 28, 16, 20, 20]
    .map(
      (width, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`
    )
    .join("");

  const files: Record<string, Uint8Array> = {
    "[Content_Types].xml":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/tables/table1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.table+xml"/>
</Types>`),
    "_rels/.rels":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    "xl/workbook.xml":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Gastos pendientes" sheetId="1" r:id="rId1"/></sheets>
</workbook>`),
    "xl/_rels/workbook.xml.rels":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    "xl/styles.xml":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2"><numFmt numFmtId="164" formatCode="yyyy-mm-dd"/><numFmt numFmtId="165" formatCode="#,##0.00 &quot;€&quot;"/></numFmts>
  <fonts count="1"><font><sz val="11"/><name val="Aptos"/></font></fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
    "xl/worksheets/sheet1.xml":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastColumn}${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="18"/>
  <cols>${columnWidths}</cols>
  <sheetData>${sheetRows}</sheetData>
  <tableParts count="1"><tablePart r:id="rId1"/></tableParts>
</worksheet>`),
    "xl/worksheets/_rels/sheet1.xml.rels":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/table" Target="../tables/table1.xml"/>
</Relationships>`),
    "xl/tables/table1.xml":
      strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<table xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" id="1" name="GastosPendientes" displayName="GastosPendientes" ref="${tableRange}" totalsRowShown="0">
  <autoFilter ref="${tableRange}"/>
  <tableColumns count="${ALLOWANCES_EXPORT_HEADERS.length}">${ALLOWANCES_EXPORT_HEADERS.map(tableColumnXml).join("")}</tableColumns>
  <tableStyleInfo name="TableStyleMedium2" showFirstColumn="0" showLastColumn="0" showRowStripes="1" showColumnStripes="0"/>
</table>`),
  };
  return zipSync(files, { level: 6 });
}

export const ALLOWANCES_XLSX_MIME_TYPE = XLSX_MIME_TYPE;
